import { useEffect, useRef, useState } from "react";
import { Input } from "../ui/input";
import api from "../../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Button } from "../ui/button";
import Pagination from "../ui/pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Loader } from "../layout/Loader";
import { Switch } from "../ui/switch";
import { Plus, X } from "lucide-react";
import { CustomSelect } from "../ui/custom-select";
import { useAuth } from "../../lib/authContext";
import AddProductStockModal from "./AddProductStockModal";

interface StockRow {
  id: number;
  name: string | null;
  sp_id: number;
  tp_code: string | null;
  mfr: string | null;
  stock: number | null;
  is_auto_stocked: boolean | null;
}

interface UpdateStockModal {
  open: boolean;
  row: StockRow | null;
}

export default function ProductsStock() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<StockRow[]>([]);
  const [entities, setEntities] = useState<any[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<number | null>(null);
  const { userEntities, user, userRole } = useAuth();

  // Update Stock Modal state
  const [modal, setModal] = useState<UpdateStockModal>({ open: false, row: null });
  const [newStock, setNewStock] = useState<string>("");
  const [reason, setReason] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const newStockRef = useRef<HTMLInputElement>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const getData = async () => {
    if (!selectedEntity || !user?.id) return;
    setLoading(true);
    try {
      const res: any = await api.getProductStock(
        search,
        page,
        limit,
        selectedEntity,
        user.id,
      );
      setRows(res.data || []);
      setTotal(res.totalCount || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userEntities && userEntities.length > 0) {
      const isSuperAdmin = userRole?.role_name === "Super Admin";
      const formatted = userEntities.map((e) => ({
        value: e.entity_id,
        label: isSuperAdmin && (e as any).is_active === false
          ? `${e.name} (${e.branch_id ?? ""}) [Inactive]`
          : `${e.name} (${e.branch_id ?? ""})`,
      }));
      setEntities(formatted);
      if (!selectedEntity && formatted.length > 0) {
        setSelectedEntity(formatted[0].value);
      }
    } else {
      setEntities([]);
    }
  }, [userEntities, userRole]);

  useEffect(() => {
    if (selectedEntity) getData();
  }, [page, limit]);

  useEffect(() => {
    if (selectedEntity && search.length > 2) getData();
  }, [search]);

  useEffect(() => {
    setPage(1);
    if (selectedEntity) getData();
  }, [selectedEntity]);

  // Open the Update Stock modal
  const openUpdateModal = (row: StockRow) => {
    setModal({ open: true, row });
    setNewStock(row.stock !== null && row.stock !== undefined ? String(row.stock) : "0");
    setReason("");
    setModalError(null);
    setTimeout(() => newStockRef.current?.focus(), 100);
  };

  const closeModal = () => {
    setModal({ open: false, row: null });
    setNewStock("");
    setReason("");
    setModalError(null);
  };

  const handleUpdateStock = async () => {
    if (!modal.row) return;

    const parsedStock = parseInt(newStock, 10);
    if (newStock === "" || isNaN(parsedStock) || parsedStock < 0) {
      setModalError("Please enter a valid non-negative stock value.");
      return;
    }
    if (!reason.trim()) {
      setModalError("Reason is mandatory.");
      return;
    }

    setModalLoading(true);
    setModalError(null);
    try {
      const res: any = await api.updateProductStock({
        id: modal.row.id,
        new_stock: parsedStock,
        reason: reason.trim(),
        updated_by: user?.email || user?.id || "unknown",
      });

      if (res?.success) {
        // Optimistically update the row in the table
        setRows((prev) =>
          prev.map((r) =>
            r.id === modal.row!.id ? { ...r, stock: parsedStock } : r
          )
        );
        closeModal();
      } else {
        setModalError(res?.error || "Failed to update stock. Please try again.");
      }
    } catch (err: any) {
      setModalError(err?.message || "An unexpected error occurred.");
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-4 bg-white rounded-md shadow-md dark:bg-gray-800">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-black dark:text-white">Products Stock</h1>
        <Button
          id="add-product-stock-btn"
          onClick={() => setAddModalOpen(true)}
          className="flex items-center gap-1.5 bg-black text-white hover:bg-black/90 dark:bg-white dark:text-black dark:hover:bg-white/90 shadow-sm"
        >
          <Plus size={16} />
          Add Product
        </Button>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex w-1/2 items-center gap-4 my-2">
          <div className="w-1/2">
            <CustomSelect
              options={entities}
              value={selectedEntity as any}
              onChange={(val: any) => setSelectedEntity(val)}
              placeholder="Select Pharmacy"
            />
          </div>
          <div className="w-1/2 relative">
            <Input
              placeholder="Search by Name or TP CODE"
              id="search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button
              className={`absolute right-0 top-0 bg-none ${search.length > 0 ? "cursor-pointer" : "cursor-not-allowed"}`}
              onClick={() => {
                setSearch("");
                if (selectedEntity) getData();
              }}
              disabled={search.length === 0}
            >
              <X size={20} />
            </Button>
          </div>
        </div>
      </div>

      {selectedEntity ? (
      <div className="w-full bg-white rounded-md shadow-md dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>SP ID</TableHead>
              <TableHead>TP CODE</TableHead>
              <TableHead>Manufacturer</TableHead>
              <TableHead>Stock</TableHead>
              <TableHead>IsAutoStock</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center">
                  <Loader />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.sp_id}</TableCell>
                  <TableCell>{row.tp_code}</TableCell>
                  <TableCell>{row.mfr}</TableCell>
                  <TableCell>
                    <span className="font-semibold">
                      {row.stock !== null && row.stock !== undefined ? row.stock : "—"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={!!row.is_auto_stocked}
                      onCheckedChange={async (checked: boolean) => {
                        const prev = row.is_auto_stocked;
                        setRows((r) => r.map((x) => (x.id === row.id ? { ...x, is_auto_stocked: checked } : x)));
                        const res = await api.updateProductAutoStock(row.id, checked);
                        if (!res?.success) {
                          setRows((r) => r.map((x) => (x.id === row.id ? { ...x, is_auto_stocked: prev } : x)));
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openUpdateModal(row)}
                    >
                      Update Stock
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      ) : (
        <div className="mt-4 border rounded-md dark:border-gray-700 px-4 py-6 text-lg font-bold">
          Please select the pharmacy to view the Products Stock
        </div>
      )}

      <div className="flex justify-between items-center mt-4">
        <div className="w-1/6">
          <Select value={limit.toString()} onValueChange={(value) => setLimit(Number(value))}>
            <SelectTrigger>
              <SelectValue placeholder="Select limit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-gray-500 mt-2">items per page</span>
        </div>
        <Pagination page={page} totalPages={Math.ceil((total || 0) / limit)} onPageChange={setPage} />
      </div>

      {/* Update Stock Modal */}
      {modal.open && modal.row && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Update Stock</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">
                  {modal.row.name || `Product #${modal.row.id}`}
                </p>
              </div>
              <button
                onClick={closeModal}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Close modal"
              >
                <X size={18} className="text-gray-500" />
              </button>
            </div>

            {/* Current Stock Info */}
            <div className="mb-5 p-4 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium mb-1">
                Current Stock
              </p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {modal.row.stock !== null && modal.row.stock !== undefined
                  ? modal.row.stock
                  : "0"}
              </p>
            </div>

            {/* New Stock Input */}
            <div className="mb-4">
              <label
                htmlFor="new-stock-input"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
              >
                New Stock <span className="text-red-500">*</span>
              </label>
              <Input
                id="new-stock-input"
                ref={newStockRef}
                type="number"
                min="0"
                placeholder="Enter new stock quantity"
                value={newStock}
                onChange={(e) => { setNewStock(e.target.value); setModalError(null); }}
                className="w-full"
              />
            </div>

            {/* Reason Input */}
            <div className="mb-5">
              <label
                htmlFor="reason-input"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
              >
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                id="reason-input"
                rows={3}
                placeholder="Enter reason for stock update (e.g. Physical count correction, new stock received…)"
                value={reason}
                onChange={(e) => { setReason(e.target.value); setModalError(null); }}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            {/* Error */}
            {modalError && (
              <p className="text-sm text-red-500 mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
                {modalError}
              </p>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={closeModal}
                disabled={modalLoading}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleUpdateStock}
                disabled={modalLoading}
              >
                {modalLoading ? "Updating…" : "Update Stock"}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Add Product Modal */}
      <AddProductStockModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={() => {
          setAddModalOpen(false);
          getData();
        }}
        entities={entities}
        defaultEntityId={selectedEntity}
      />
    </div>
  );
}

